import type { Json } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';
import { cellColorKey } from './cellColorUtils';

/** Same `field|rowKey` coordinate the cell colors use. */
export const cellChangeKey = cellColorKey;

/** Field prefix of the virtual "last change time" column that follows `field`. */
export const CHANGE_TIME_FIELD_PREFIX = '__changed_at:';
export const changeTimeField = (field: string): string => `${CHANGE_TIME_FIELD_PREFIX}${field}`;
export const changeTimeSource = (field: string | undefined): string | null =>
  field?.startsWith(CHANGE_TIME_FIELD_PREFIX) ? field.slice(CHANGE_TIME_FIELD_PREFIX.length) : null;

export interface CellValueReport {
  readonly rowKey: string;
  readonly field: string;
  readonly value: Json;
}

export interface CellChanges {
  /** Last value the database holds for each cell, to report only differences. */
  readonly values: Map<string, string>;
  /** When each cell's value last changed; absent while the cell has had no value. */
  readonly times: Map<string, Date>;
}

/** A value as the comparison key both sides agree on. */
export const serializeCellValue = (value: unknown): string => JSON.stringify(value ?? null);

/** Cells per report: one call for a whole market of prices runs past the statement timeout. */
const REPORT_BATCH = 500;

export async function loadCellChanges(
  orgId: string,
  gridKey: string,
  documentId?: string | null,
): Promise<CellChanges> {
  const values = new Map<string, string>();
  const times = new Map<string, Date>();
  // One call: reading the table page by page checks sheet access per cell and per page,
  // and a large grid ran past the statement timeout - then it showed and recorded nothing.
  const { data, error } = await supabase.rpc('load_cell_changes', {
    p_org_id: orgId,
    p_grid_key: gridKey,
    ...(documentId ? { p_document_id: documentId } : {}),
  });
  if (error) throw error;
  const rows = (Array.isArray(data) ? data : []) as Array<{ row_key: string; field: string; value: Json; changed_at: string | null }>;
  for (const row of rows) {
    const key = cellChangeKey(row.field, row.row_key);
    values.set(key, serializeCellValue(row.value));
    if (row.changed_at) times.set(key, new Date(row.changed_at));
  }
  return { values, times };
}

/**
 * Report the values the grid shows. The database stamps a cell the first time
 * it holds a value and whenever the value differs from the stored one; the
 * cells it stamped come back.
 */
export async function recordCellValues(
  orgId: string,
  gridKey: string,
  cells: readonly CellValueReport[],
  documentId?: string | null,
): Promise<Map<string, Date>> {
  const stamped = new Map<string, Date>();
  for (let from = 0; from < cells.length; from += REPORT_BATCH) {
    const { data, error } = await supabase.rpc('record_cell_values', {
      p_org_id: orgId,
      p_grid_key: gridKey,
      p_cells: cells.slice(from, from + REPORT_BATCH).map((cell) => ({ row_key: cell.rowKey, field: cell.field, value: cell.value })) as unknown as Json,
      ...(documentId ? { p_document_id: documentId } : {}),
    });
    if (error) throw error;
    for (const row of data ?? []) stamped.set(cellChangeKey(row.field, row.row_key), new Date(row.changed_at));
  }
  return stamped;
}
